import axios from './axiosConfig'
import { DiningTable } from './diningHalls'

interface TablesResponse {
 status: string
 data: {
 tables: DiningTable[]
 status_counts: {
 available: number
 occupied: number
 reserved: number
 closed: number
 }
 }
 message?: string
}

type ApiOne = { status: string; data: DiningTable; message?: string }

export interface TableOrderDraftItem {
 product_id: number
 quantity: number
 unit_id?: number | null
 unit_price: number
 discount?: number
 variant_id?: string | null
 selected_options?: Array<{ product_option_group_id: number; option_ids: number[] }>
 client_cart_line_id?: string | null
 kitchen_route_type?: 'screen' | 'printer' | 'none' | null
 kitchen_station_id?: string | null
 kitchen_printer_id?: string | null
 kitchen_printed_quantity?: number
 kitchen_printed_at?: string | null
 notes?: string | null
}

export interface TableOrderDraftPayload {
 price_defining_options_version?: number
  /** Idempotency key for offline queue retries (optional). */
  client_op_id?: string | null
  /** ISO timestamp from last successful server sync; detects concurrent edits from another device. */
  expected_sale_updated_at?: string | null
  expected_cart_revision?: number | null
  /** Sale identity from the selected table; prevents an old draft from creating a new session. */
  expected_order_id?: number | string | null
  /** Server table generation; fences delayed first writes after an empty-cart clear. */
  expected_table_generation?: number | null
 items: TableOrderDraftItem[]
 subtotal?: number
 tax?: number
 discount?: number
 promotion_discount?: number
 coupon_id?: string | null
 coupon_discount?: number
 service_charge?: number
 total?: number
 customer_id?: number | null
 notes?: string | null
}

export const diningTablesAPI = {
 listForHall: async (hallId: string) => {
 const response = await axios.get<{ status: string; data: DiningTable[] }>(`/dining-halls/${hallId}/tables`)
 return response.data
 },

 listForBranch: async (branchId?: string, status?: string) => {
 const params: Record<string, string> = {}
 if (status) params.status = status
 const url = branchId ? `/branches/${branchId}/tables` : '/pos/tables'
 const response = await axios.get<TablesResponse>(url, {
 params,
 ...(branchId ? { headers: { 'X-Branch-Id': branchId } } : {}),
 })
 return response.data
 },

 listForPos: async (branchId: string, status?: string) => {
 const params: Record<string, string> = {}
 if (status) params.status = status
 const response = await axios.get<TablesResponse>('/pos/tables', {
 params,
 headers: { 'X-Branch-Id': branchId },
 })
 return response.data
 },

 getActiveOrder: async (tableId: string) => {
 const response = await axios.get(`/pos/tables/${tableId}/order`)
 return response.data
 },

 syncOrderDraft: async (
  tableId: string,
  payload: TableOrderDraftPayload,
  options: { keepalive?: boolean } = {},
 ) => {
 const requestConfig = options.keepalive
   ? { adapter: 'fetch' as const, fetchOptions: { keepalive: true } }
   : undefined
 const response = await axios.put(`/pos/tables/${tableId}/order/draft`, payload, requestConfig)
 return response.data
 },

 transferOrder: async (sourceTableId: string, targetTableId: string) => {
 const response = await axios.post(`/pos/tables/${sourceTableId}/transfer`, { target_table_id: targetTableId })
 return response.data
 },

 mergeOrder: async (sourceTableId: string, targetTableId: string) => {
 const response = await axios.post(`/pos/tables/${sourceTableId}/merge`, { target_table_id: targetTableId })
 return response.data
 },

 unmergeOrder: async (targetTableId: string, sourceTableId: string) => {
 const response = await axios.post(`/pos/tables/${targetTableId}/unmerge`, {
 source_table_id: sourceTableId,
 })
 return response.data
 },

 settleOrder: async (
 tableId: string,
 payload: {
 payment_type:
 | 'cash'
 | 'card'
 | 'credit'
 | 'layaway'
 | 'split'
 | 'wallet'
 | 'electronic_wallet'
 | 'instapay'
 | 'bank_transfer'
 | 'payment_gateway'
 paid: number
 payment_lines?: {
 financial_account_id?: string | null
 vault_id?: string | null
 amount: number
 payment_method?: 'cash' | 'card' | 'wallet' | 'electronic_wallet' | 'instapay' | 'bank_transfer' | 'payment_gateway'
 reference?: string | null
 client_line_id?: string
 }[] | null
 customer_id?: number | null
 loyalty_points_redeemed?: number
 loyalty_discount?: number
 discount?: number
 cart_discount?: number
 promotion_discount?: number
 coupon_id?: string | null
 coupon_discount?: number
 service_charge?: number
 notes?: string | null
 layaway_terms?: Record<string, unknown> | null
 client_op_id?: string
 cash_tendered?: number
 pos_register_id?: string | null
 register_session_id?: string | null
 },
 ) => {
 const response = await axios.post(`/pos/tables/${tableId}/order/settle`, payload)
 return response.data
 },

 dispatchKitchen: async (
 tableId: string,
 payload: { client_op_id?: string | null } = {},
 ) => {
 const response = await axios.post(`/pos/tables/${tableId}/order/kitchen`, payload)
 return response.data?.data ?? response.data
 },

 reprintKitchen: async (
 tableId: string,
 payload: { client_op_id?: string | null; sale_item_ids?: number[] } = {},
 ) => {
 const response = await axios.post(`/pos/tables/${tableId}/order/kitchen/reprint`, payload)
 return response.data?.data ?? response.data
 },

 syncReservations: async (
 tableId: string,
 items: Array<{ product_id: number; variant_id?: string | null; unit_id?: number | null; quantity: number }>,
 ) => {
 const response = await axios.put(`/pos/tables/${tableId}/reservations/sync`, { items })
 return response.data
 },

 clearReservations: async (tableId: string) => {
 const response = await axios.post(`/pos/tables/${tableId}/reservations/clear`)
 return response.data
 },

 get: async (tableId: string) => {
 const response = await axios.get<ApiOne>(`/tables/${tableId}`)
 return response.data
 },

 create: async (hallId: string, payload: Record<string, unknown>) => {
 const response = await axios.post<ApiOne>(`/dining-halls/${hallId}/tables`, payload)
 return response.data
 },

 update: async (tableId: string, payload: Record<string, unknown>) => {
 const response = await axios.put<ApiOne>(`/tables/${tableId}`, payload)
 return response.data
 },

 updateStatus: async (tableId: string, status: string) => {
 const response = await axios.patch(`/tables/${tableId}/status`, { status })
 return response.data
 },

 assignOrder: async (tableId: string, saleId: string) => {
 const response = await axios.post(`/tables/${tableId}/assign-order`, { sale_id: saleId })
 return response.data
 },

 release: async (tableId: string) => {
 const response = await axios.post(`/tables/${tableId}/release`)
 return response.data
 },

 releaseForPos: async (tableId: string) => {
 const response = await axios.post(`/pos/tables/${tableId}/release`)
 return response.data
 },

 delete: async (tableId: string) => {
 const response = await axios.delete(`/tables/${tableId}`)
 return response.data
 },
}
